<template>
  <component
    :is="data.linkHref ? 'a' : 'div'"
    :class="b()"
    :href="data.linkHref"
    :title="data.linkTitle"
    :target="data.linkTarget"
  >
    <div :class="b('inner')">
      <e-picture
        :class="b('image')"
        :srcset="data.image.srcset"
        :sizes="{ xs: 165 }"
        :alt="data.image.alt"
        :fallback="data.image.fallback"
        width="160"
        height="160"
      />
      <div :class="b('title')">
        {{ data.title }}
      </div>
      <div :class="b('text')">
        {{ data.text }}
      </div>
      <div
        v-if="data.linkHref"
        :class="b('link')"
      >
        {{ data.linkText }}
        <e-icon-sprite icon="i-chevron-2--right" />
      </div>
    </div>
  </component>
</template>

<script>
  import eIconSprite from '@/elements/e-icon-sprite.vue';
  import ePicture from '@/elements/e-picture.vue';

  /**
   * SSR too! - Be careful with JS logic.
   *
   * Renders a single slider slide.
   */
  export default {
    name: 'c-slider-item-1',
    components: {
      eIconSprite,
      ePicture,
    },

    // mixins: [],

    props: {
      /**
       * Expects the components display data.
       *
       * @property {Object} data - The slide data.
       * @property {String} data.title - The slide title.
       * @property {String} data.text - The slide text.
       * @property {String} data.linkText - The text of the slide link.
       * @property {String} data.linkHref - The link of the slide link.
       * @property {String} data.linkTitle - The title of the slide link.
       * @property {String} [data.linkTarget] - The target of the slide link.
       * @property {String} data.image.src - The source of the item image.
       * @property {String} data.image.alt - The alt text for the item image.
       */
      data: {
        type: Object,
        required: true,
      },
    },
    // data() {
    //   return {};
    // },

    // computed: {},
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    // methods: {},
    // render() {},
  };
</script>

<style lang="scss">
  @use 'sass:math';
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/functions';
  @use '@/setup/scss/extends' as *;

  .c-slider-item-1 {
    $this: &;
    $border-width: 1px;

    position: relative;
    display: flex; // Needs to stay flex-direction: row; because of ratio keeper.
    flex: 0 0 auto;
    align-self: stretch;
    width: functions.rem(variables.$grid-width--xs);
    margin-right: -1 * $border-width; // Compensate missing border
    padding: functions.rem(variables.$spacing--40);
    border: $border-width solid variables.$color-grayscale--900;
    border-right-color: transparent;
    color: variables.$color-primary--1;

    @include mixins.media(sm) {
      width: functions.rem(450px);
    }

    @include mixins.media(md) {
      width: functions.rem(544px);
    }

    &:focus {
      outline: 0; // Border is already defined.
    }

    &:last-child {
      border-right-color: variables.$color-grayscale--900;
    }

    &::before {
      // Ratio keeper.
      @include mixins.media(md) {
        content: '';
        float: left;
        width: 0;
        height: 0;
        padding-top: 100%;
      }
    }

    &::after {
      // Animated gradient border. @see https://codepen.io/johnnyfekete/pen/WMoWvb
      @include mixins.z-index(front);

      position: absolute;
      top: -1 * $border-width;
      right: -1 * $border-width;
      bottom: -1 * $border-width;
      left: -1 * $border-width;
      content: '';
      opacity: 0;

      // IE11
      border: $border-width solid transparent;
      transition:
        background-size variables.$transition-duration--500 linear,
        opacity variables.$transition-duration--1000;
      pointer-events: none;

      @supports (clip-path: none) {
        border: 0;
        background: radial-gradient(
            circle at bottom left,
            variables.$color-gradient--1-0 math.div(variables.$color-gradient--1-start, 2),
            variables.$color-gradient--1-1 math.div(variables.$color-gradient--1-end, 2),
            rgba(variables.$color-gradient--1-1, 0)
          )
          no-repeat left bottom;
        background-size: 200% 200%;

        // stylelint-disable declaration-colon-newline-after
        clip-path: polygon(
          0% 100%,
          $border-width 100%,
          $border-width $border-width,
          calc(100% - #{$border-width}) $border-width,
          calc(100% - #{$border-width}) calc(100% - #{$border-width}),
          $border-width calc(100% - #{$border-width}),
          $border-width 100%,
          100% 100%,
          100% 0%,
          0% 0%
        );
        // stylelint-enable declaration-colon-newline-after

        @include mixins.media(md) {
          opacity: 1;
          background-size: 0 0;
        }
      }
    }

    &.is-active::after {
      @include mixins.media($down: md) {
        opacity: 1;
      }
    }

    body:not(.uses-touch) &:hover::after, // Safari mobile bugfix
    &:focus::after,
    .uses-touch &.is-active::after {
      opacity: 1;

      // IE11
      border-color: variables.$color-gradient--1-fallback;
      border-image-source: linear-gradient(45deg, variables.$color-gradient--1);
      border-image-slice: 1;

      @include mixins.media(md) {
        background-size: 200% 200%;
      }
    }

    &__inner {
      display: flex;
      flex-direction: column;
      align-self: flex-end;
      width: 100%; // IE11 overflow bugfix
    }

    &__image {
      display: none;

      @include mixins.media(sm) {
        display: block;
        width: functions.rem(160px);
        height: functions.rem(160px);
        margin-bottom: functions.rem(variables.$spacing--40);
        overflow: hidden;
        border-radius: 50%;
        background: url('../assets/placeholder/placeholder.svg') no-repeat center center variables.$color-grayscale--920;
        background-size: 40%;
      }
    }

    &__title {
      @extend %heading-h4;

      margin-bottom: 0;
    }

    &__text {
      margin-top: functions.rem(variables.$spacing--15);

      @include mixins.media(md) {
        max-height: 0;
        margin-top: 0;
        overflow: hidden;
        opacity: 0;
        transition:
          max-height variables.$transition-duration--500,
          margin variables.$transition-duration--400,
          opacity variables.$transition-duration--400;

        #{$this}:hover &,
        #{$this}:focus &,
        .uses-touch & {
          max-height: functions.rem(200px);
          margin-top: functions.rem(variables.$spacing--40);
          opacity: 1;
        }
      }
    }

    &__link {
      display: flex;
      align-items: center;
      margin-top: functions.rem(variables.$spacing--25);
      color: variables.$color-primary--2;
      transition: color variables.$transition-duration--200;

      &:hover,
      &:focus {
        color: variables.$color-primary--1;
      }

      .e-icon-sprite {
        @include mixins.showMoreIcon();
      }
    }
  }
</style>
